import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Wallet, RefreshCcw, Bell, Check, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutos

function formatMXN(n) {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 2,
  }).format(Number(n || 0));
}

/**
 * Widget de monitoreo de saldo Envia.com
 * - Auto-refresh cada 10 minutos
 * - Color dinámico según umbral: verde / ámbar / rojo
 * - Permite configurar umbral inline
 * - Maneja fallos graciosamente con "No disponible"
 */
export function EnviaBalanceWidget({ variant = 'full' }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [balance, setBalance] = useState(null);          // {balance, currency, last_updated} | null
  const [threshold, setThreshold] = useState(500);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const [editingThreshold, setEditingThreshold] = useState(false);
  const [thresholdInput, setThresholdInput] = useState('500');
  const [savingThreshold, setSavingThreshold] = useState(false);

  const intervalRef = useRef(null);

  const loadBalance = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/envia/admin/envia-balance`, { headers });
      setBalance(res.data);
      setUnavailable(false);
    } catch (err) {
      console.warn('Envia balance unavailable:', err?.response?.data || err?.message);
      setBalance(null);
      setUnavailable(true);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadThreshold = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/envia/admin/envia-balance/threshold`, { headers });
      const t = Number(res.data?.threshold_mxn ?? 500);
      setThreshold(t);
      setThresholdInput(String(t));
    } catch (err) {
      // silencioso, usar default 500
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carga inicial + polling cada 10 min
  useEffect(() => {
    if (!token) return;
    loadThreshold();
    loadBalance({ silent: true });
    intervalRef.current = setInterval(() => loadBalance({ silent: true }), REFRESH_INTERVAL_MS);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [token, loadBalance, loadThreshold]);

  const handleManualRefresh = async () => {
    await loadBalance();
    if (!unavailable) toast.success('Saldo actualizado');
  };

  const handleSaveThreshold = async () => {
    const n = Number(thresholdInput);
    if (!Number.isFinite(n) || n < 0) {
      toast.error('Ingresa un número válido mayor o igual a 0');
      return;
    }
    setSavingThreshold(true);
    try {
      await axios.post(
        `${API}/envia/admin/envia-balance/alert-threshold`,
        { threshold_mxn: n },
        { headers }
      );
      setThreshold(n);
      setEditingThreshold(false);
      toast.success(`Umbral de alerta actualizado a ${formatMXN(n)}`);
    } catch (err) {
      toast.error('No se pudo guardar el umbral');
    } finally {
      setSavingThreshold(false);
    }
  };

  // Determinar estilo según saldo vs umbral
  const value = balance?.balance ?? null;
  const status = (() => {
    if (unavailable || value === null) return 'unavailable';
    if (value <= 0) return 'critical';
    if (value <= threshold) return 'warning';
    return 'healthy';
  })();

  const STATUS_STYLES = {
    healthy: {
      gradient: 'from-emerald-950 via-emerald-900 to-emerald-950',
      border: 'border-emerald-600/40',
      iconBg: 'bg-emerald-500/20',
      iconColor: 'text-emerald-300',
      valueColor: 'text-emerald-300',
      badgeBg: 'bg-emerald-500/20 border-emerald-400/40 text-emerald-200',
      badgeLabel: 'Saldo saludable',
      glow: 'shadow-emerald-500/10',
    },
    warning: {
      gradient: 'from-amber-950 via-amber-900 to-amber-950',
      border: 'border-amber-600/40',
      iconBg: 'bg-amber-500/20',
      iconColor: 'text-amber-300',
      valueColor: 'text-amber-300',
      badgeBg: 'bg-amber-500/20 border-amber-400/40 text-amber-200',
      badgeLabel: 'Cerca del umbral',
      glow: 'shadow-amber-500/10',
    },
    critical: {
      gradient: 'from-rose-950 via-rose-900 to-rose-950',
      border: 'border-rose-600/40',
      iconBg: 'bg-rose-500/20',
      iconColor: 'text-rose-300',
      valueColor: 'text-rose-300',
      badgeBg: 'bg-rose-500/20 border-rose-400/40 text-rose-200',
      badgeLabel: 'Saldo agotado',
      glow: 'shadow-rose-500/10',
    },
    unavailable: {
      gradient: 'from-slate-900 via-slate-800 to-slate-900',
      border: 'border-slate-600/40',
      iconBg: 'bg-white/10',
      iconColor: 'text-slate-300',
      valueColor: 'text-slate-200',
      badgeBg: 'bg-white/10 border-white/15 text-slate-300',
      badgeLabel: 'No disponible',
      glow: 'shadow-black/40',
    },
  };

  const s = STATUS_STYLES[status];
  const isCompact = variant === 'compact';

  return (
    <Card
      data-testid="envia-balance-widget"
      data-status={status}
      className={`relative overflow-hidden border ${s.border} bg-gradient-to-br ${s.gradient} shadow-lg ${s.glow}`}
    >
      <CardContent className={isCompact ? 'p-4' : 'p-5'}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-md ${s.iconBg} flex-shrink-0`}>
              <Wallet className={`h-4 w-4 ${s.iconColor}`} />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
                Saldo Envia.com
              </p>
              {loading ? (
                <Skeleton className="h-7 w-28 mt-1 bg-white/10" />
              ) : status === 'unavailable' ? (
                <p
                  className={`mt-0.5 font-semibold tabular-nums flex items-center gap-1.5 ${s.valueColor} ${isCompact ? 'text-lg' : 'text-2xl'}`}
                  data-testid="balance-value-unavailable"
                >
                  <AlertCircle className="h-4 w-4" />
                  No disponible
                </p>
              ) : (
                <p
                  className={`mt-0.5 font-semibold tabular-nums ${s.valueColor} ${isCompact ? 'text-xl' : 'text-2xl'}`}
                  data-testid="balance-value"
                >
                  {formatMXN(value)}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span
              data-testid="balance-status-badge"
              className={`hidden sm:inline-flex items-center justify-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${s.badgeBg}`}
            >
              {s.badgeLabel}
            </span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={handleManualRefresh}
              disabled={refreshing}
              data-testid="balance-refresh-btn"
              className="h-8 w-8 text-slate-400 hover:text-slate-100 hover:bg-white/10"
              aria-label="Actualizar saldo"
            >
              <RefreshCcw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Footer line: umbral + última actualización */}
        <div className="mt-3 pt-3 border-t border-white/8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[11px]">
          {editingThreshold ? (
            <div className="flex items-center gap-1.5" data-testid="threshold-edit-row">
              <Bell className="h-3 w-3 text-slate-400 flex-shrink-0" />
              <span className="text-slate-400">Alertar si baja de</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={thresholdInput}
                onChange={(e) => setThresholdInput(e.target.value)}
                className="h-7 w-24 bg-white/5 border-white/15 text-slate-100 text-xs"
                data-testid="threshold-input"
              />
              <span className="text-slate-400">MXN</span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={handleSaveThreshold}
                disabled={savingThreshold}
                data-testid="threshold-save-btn"
                className="h-6 w-6 text-emerald-300 hover:bg-emerald-500/15"
                aria-label="Guardar umbral"
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => { setEditingThreshold(false); setThresholdInput(String(threshold)); }}
                data-testid="threshold-cancel-btn"
                className="h-6 w-6 text-slate-400 hover:bg-white/10"
                aria-label="Cancelar"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditingThreshold(true)}
              data-testid="threshold-edit-btn"
              className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-100 transition-colors duration-150 self-start sm:self-auto"
            >
              <Bell className="h-3 w-3" />
              <span>Alerta:&nbsp;<span className="tabular-nums text-slate-200">{formatMXN(threshold)}</span></span>
              <span className="text-slate-500 underline-offset-2 hover:underline">Configurar</span>
            </button>
          )}

          {balance?.last_updated && !unavailable && (
            <span className="text-slate-500 tabular-nums" data-testid="balance-last-updated">
              Actualizado {new Date(balance.last_updated).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {unavailable && (
            <span className="text-slate-500" data-testid="balance-unavailable-hint">
              Consulta el panel de Envia.com
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default EnviaBalanceWidget;
